import type { DateRange } from '#core/types/date-filter'
import { DEFAULT_BI_COINGECKO_ID } from '@forgd/shared/constants'
import { formatValue } from '@forgd/formatter'
import { getMinutes, setMilliseconds, setMinutes, setSeconds, subDays } from 'date-fns'

export interface TaSelectedCoin {
  id: string
  name: string
  symbol: string
  image: string | null
}

export const useTaStore = defineStore('analyze-trade-activity', () => {
  const auth = useAuth()

  const dateRangeRef = ref<DateRange>({
    start: new Date(),
    end: new Date(),
    timeframe: '7d',
  })

  const dateRange = computed({
    get: () => {
      return dateRangeRef.value
    },
    set: (val: DateRange) => {
      if (val.timeframe !== 'custom') {
        // For timeframe: round current time to nearest 5-minute interval and go back 24 hours
        // This ensures consistent time boundaries and we will work with the data that is processed (e.g., 17:38 -> 17:35, 18:03 -> 18:00)
        const timeframeInDays = getTimeframeInDays(val.timeframe) - 1 // -1 because we want to include the current day
        const now = new Date()
        const minutes = getMinutes(now)
        const roundedMinutes = Math.floor(minutes / 5) * 5
        const end = setMilliseconds(setSeconds(setMinutes(now, roundedMinutes), 0), 0)

        const start = subDays(end, timeframeInDays)

        dateRangeRef.value = {
          start,
          end,
          timeframe: val.timeframe,
        }
        return
      }

      dateRangeRef.value = val
    },
  })

  const dateSelectedLabel = computed(() => {
    if (dateRange.value.timeframe === 'custom') {
      return `${formatValue(dateRange.value.start, 'date')} Avg.`
    }
    if (daysLookback.value === 1) {
      return 'Prev. 24h Avg.'
    }
    return `${daysLookback.value}-Day Avg.`
  })

  const daysLookback = computed(() => {
    if (dateRange.value.timeframe === 'custom') {
      return 1
    }
    return getTimeframeInDays(dateRange.value.timeframe)
  })

  const apiDate = computed(() => dateRange.value.end.toISOString())

  // Token selection: null means the default coin (the project's own token when it has a listed one)
  const selectedCoin = ref<TaSelectedCoin | null>(null)

  // The project's own token — only when it is listed, so unlisted (pre-TGE) and
  // research projects default to the default coin instead of an empty dashboard
  const projectCoin = computed<TaSelectedCoin | null>(() => {
    if (!auth.project?.tokenListed || !auth.project.coingeckoId || auth.project.isResearch) {
      return null
    }
    return {
      id: auth.project.coingeckoId,
      name: auth.project.name,
      symbol: auth.project.ticker || auth.project.name,
      image: auth.project.image ?? null,
    }
  })

  function selectCoin(coin: TaSelectedCoin | null) {
    if (!coin || coin.id === projectCoin.value?.id) {
      selectedCoin.value = null
      return
    }
    selectedCoin.value = coin
  }

  // Metadata of the default coin, fetched lazily for anonymous visitors and
  // projects without their own coin, so titles and the combobox trigger can
  // show the coin whose data is actually displayed
  const defaultCoin = ref<TaSelectedCoin | null>(null)
  const needsDefaultCoin = computed(() => !selectedCoin.value && !projectCoin.value)
  watch(needsDefaultCoin, async (needed) => {
    if (!needed || defaultCoin.value) {
      return
    }
    const coins = await apiFetch('/business-intelligence/coins', {
      params: {
        query: { search: DEFAULT_BI_COINGECKO_ID, limit: 50 },
      },
    })
    defaultCoin.value = coins?.find(coin => coin.id === DEFAULT_BI_COINGECKO_ID) ?? null
  }, { immediate: true })

  // The coin whose data is currently displayed
  const activeCoin = computed(() => selectedCoin.value ?? projectCoin.value ?? defaultCoin.value)

  // Whether the dashboard is showing the project's own (listed) token
  const isViewingOwnToken = computed(() => !selectedCoin.value && Boolean(projectCoin.value))

  // Not derived from activeCoin so data loading is never blocked on the default-coin metadata fetch
  const apiCoingeckoId = computed(
    () => selectedCoin.value?.id ?? projectCoin.value?.id ?? DEFAULT_BI_COINGECKO_ID,
  )

  const displayTicker = computed(() => {
    if (selectedCoin.value) {
      return selectedCoin.value.symbol.toUpperCase()
    }
    if (projectCoin.value) {
      return auth.project?.ticker || auth.project?.name
    }
    return defaultCoin.value?.symbol.toUpperCase()
  })

  const displayImage = computed(() => activeCoin.value?.image ?? null)

  return {
    dateRange,
    dateSelectedLabel,
    daysLookback,
    apiDate,
    selectedCoin,
    selectCoin,
    activeCoin,
    isViewingOwnToken,
    apiCoingeckoId,
    displayTicker,
    displayImage,
  }
})
